import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { usePaymentHubDebtsLoader } from '../loaders/PaymentHubDebtsData';
import { PaymentHubDebt, PaymentHubDebtStatus } from '../types';
import {
  Button,
  ColumnTable,
  DisplayInfo,
  Icon,
  Loading,
  Table,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import DisplayError from '../../../../components/info/DisplayError';
import dayjs from 'dayjs';
import Tooltip from '../../../../components/tooltip/Tooltip';
import RegisterPaymentModal, {
  RegisterPaymentModalMethods,
} from './RegisterPaymentModal';

const IdentificationTable = () => {
  const { t } = useTranslation(TranslationsKeys.PAYMENT_HUB_DEBTS);
  const registerPaymentModalRef = useRef<RegisterPaymentModalMethods>(null);
  const { query, result, loading, refresh } = usePaymentHubDebtsLoader();

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }
    return result.data ?? [];
  }, [result]);

  const handleCreatePayment = useCallback(
    (row: PaymentHubDebt) => {
      if (registerPaymentModalRef.current) {
        registerPaymentModalRef.current.open(row.id, query);
      }
    },
    [query],
  );

  const columns = useMemo<ColumnTable<PaymentHubDebt>[]>(
    () => [
      {
        columnName: 'idToDisplay',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.idToDisplay`),
      },
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.type`),
      },
      {
        columnName: 'details',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.details`),
      },
      {
        columnName: 'installment',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.installment`),
      },
      {
        columnName: 'installmentDueDate',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.installmentDueDate`),
        cellFormat: ({ value }) => {
          if (!value) {
            return '-';
          }
          const date = dayjs(value);
          return date.utc().format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'amount',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.amount`),
        cellFormat: ({ row }) => {
          return row?.amount?.value?.toLocaleString('es-CL', {
            style: 'currency',
            currency: row?.amount?.currency ?? 'CLP',
          });
        },
      },
      {
        columnName: 'interests',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.interests`),
        cellFormat: ({ row }) => {
          return row?.interests?.value?.toLocaleString('es-CL', {
            style: 'currency',
            currency: row?.interests?.currency ?? 'CLP',
          });
        },
      },
      {
        columnName: 'finalAmount',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.finalAmount`),
        cellFormat: ({ row }) => {
          return (
            <strong>
              {row?.finalAmount?.value?.toLocaleString('es-CL', {
                style: 'currency',
                currency: row?.finalAmount?.currency ?? 'CLP',
              })}
            </strong>
          );
        },
      },
      {
        columnName: 'status',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.status`),
        width: 120,
        cellFormat: ({ value, row }) => {
          if (value === PaymentHubDebtStatus.InProcess) {
            return (
              <Tooltip
                id={`debt_${row.id}`}
                placement="bottom"
                text={t(`table.tooltips.in_process`)}
              >
                <Icon name="circle" />
              </Tooltip>
            );
          }
          if (value === PaymentHubDebtStatus.Payed) {
            return (
              <Tooltip
                id={`debt_${row.id}`}
                placement="bottom"
                text={t(`table.tooltips.payed`)}
              >
                <Icon name="success" />
              </Tooltip>
            );
          }
          return '';
        },
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: '',
        width: 180,
        cellFormat: ({ row }) => {
          return (
            <Button
              size="sm"
              text={t(`table.actions.registerPresentialPayment`)}
              disabled={row.status !== PaymentHubDebtStatus.Pending}
              onClick={() => handleCreatePayment(row)}
            />
          );
        },
      },
    ],
    [t, handleCreatePayment],
  );

  if (loading) {
    return <Loading textBody="" />;
  }

  if (
    !query?.identificationDocument ||
    !query?.identificationDocumentType ||
    !query?.identificationDocumentCountry
  ) {
    return (
      <DisplayInfo
        title={t('noFiltersSelected.title')}
        textBody={t('noFiltersSelected.description')}
      />
    );
  }

  if (!result?.success) {
    return (
      <DisplayError
        title={t('queryErrors.title')}
        textBody={t('queryErrors.description')}
      />
    );
  }

  return (
    <div className="p-5">
      <p>{t('tablePrefix')}</p>
      <Table columns={columns} data={items} />
      <RegisterPaymentModal ref={registerPaymentModalRef} onSave={refresh} />
    </div>
  );
};

export default IdentificationTable;
